import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Check,
  CalendarRange,
  Clock,
  Filter,
  Loader2,
  MapPin,
  MoreHorizontal,
  Pencil,
  Search,
  Share2,
  Sparkles,
  Trash2,
  UserSearch,
  Users,
  Wallet,
  CircleOff,
} from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { getProjects } from "reducer/projectsSlice";
import { API } from "service/Interceptor";
import { decodeBriefPlainText, serverFiles } from "utils/utils";
import {
  getCreatorBriefs,
  isCreatorBriefPost,
  mapPostToOpenBrief,
  removeCreatorBrief,
  briefApplicantsPath,
  isApplicationsClosed,
  setBriefApplicationsClosed,
} from "utils/creatorBriefs";
import toast from "components/Toast/toast";
import BriefModal from "components/app/BriefModal";
import ApplyDialog from "components/app/ApplyDialog";
import { useAppliedJobIds } from "utils/appliedJobs";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const INDUSTRIES = [
  "Fashion",
  "Beauty",
  "Jewellery",
  "Lifestyle",
  "Product",
  "Technology",
  "Media, Marketing, Publishing & PR",
  "Designing",
  "Entertainment",
  "Events & Weddings",
  "Hospitality",
  "Advertising",
  "Architecture",
  "Production",
  "Music",
];

const SidebarCard = ({ children }) => (
  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
    {children}
  </div>
);

const FilterRow = ({ active, onClick, label, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
      active
        ? "bg-[#6C63F5]/15 text-[#8b83ff] font-medium"
        : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
    }`}
  >
    <span className="truncate pr-2">{label}</span>
    {typeof count === "number" && (
      <span className="text-xs text-zinc-500 shrink-0">{count}</span>
    )}
  </button>
);

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ src, name, size = 40 }) =>
  src ? (
    <img
      src={src}
      alt={name}
      style={{ height: size, width: size }}
      className="rounded-full object-cover shrink-0"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div
      style={{ height: size, width: size }}
      className="avatar-fallback rounded-full shrink-0 flex items-center justify-center font-semibold text-sm"
    >
      {initials(name)}
    </div>
  );

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return "";
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const formatBudget = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 100000)
    return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)}L`;
  if (num >= 1000) return `₹${Math.round(num / 1000)}k`;
  return `₹${num}`;
};

const formatDuration = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (!Number.isNaN(num) && num > 0) {
    return `${num} day${num === 1 ? "" : "s"}`;
  }
  return String(n);
};

const stripHtml = (value = "") => decodeBriefPlainText(value);

const parseBriefExtras = (description = "") => {
  const text = stripHtml(description);
  const work =
    text.match(/Work style:\s*([^\n]+)/i)?.[1]?.trim() || null;
  const looking =
    text.match(/Looking for:\s*([^\n]+)/i)?.[1]?.trim() || null;
  const city =
    text.match(/City:\s*([^\n]+)/i)?.[1]?.trim() || null;
  const budget =
    text.match(/Budget:\s*([^\n]+)/i)?.[1]?.trim() || null;
  const timeline =
    text.match(/Timeline:\s*([^\n]+)/i)?.[1]?.trim() || null;
  const dates =
    text.match(/Project dates:\s*([^\n]+)/i)?.[1]?.trim() || null;
  const needLine =
    text
      .split("\n")
      .map((line) => line.trim())
      .find(
        (line) =>
          line &&
          !/^(looking for|work style|city|budget|timeline|project dates|industry|jobid|applicationsclosed):/i.test(
            line
          )
      ) || null;
  return { work, looking, city, budget, timeline, dates, needLine };
};

const ProjectCard = ({
  project,
  i,
  isBrand,
  isOwner,
  onApply,
  onDelete,
  onToggleClosed,
  compactOwnerActions = false,
}) => {
  const navigate = useNavigate();
  const { hasApplied } = useAppliedJobIds();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isCreatorBrief = !!project?.isCreatorBrief;
  const brand =
    project?.companyJobRespDto?.name || project?.company?.name || "Brand";
  const logoPath =
    project?.companyJobRespDto?.logo || project?.company?.logo;
  const avatarSrc = logoPath ? serverFiles(logoPath) : null;
  const industry =
    (Array.isArray(project?.industry) && project.industry[0]) ||
    (Array.isArray(project?.companyJobRespDto?.categoryNames) &&
      project.companyJobRespDto.categoryNames[0]) ||
    (Array.isArray(project?.category) && project.category[0]?.name) ||
    project?.category?.name ||
    null;

  const extras = parseBriefExtras(project?.description);
  // Prefer the full need from description (Post a brief), fall back to title
  const rawTitle = decodeBriefPlainText(project?.jobTitle || "");
  const needText =
    (extras.needLine && extras.needLine.length >= rawTitle.length
      ? extras.needLine
      : null) ||
    rawTitle ||
    extras.needLine ||
    stripHtml(project?.description) ||
    "Open brief";
  const creativeType =
    (Array.isArray(project?.skillsRequired) &&
      project.skillsRequired.filter(Boolean).join(", ")) ||
    extras.looking ||
    null;
  const workStyle =
    project?.workStyle ||
    project?.work_style ||
    extras.work ||
    null;
  // Prefer city typed into the brief (description) over the job FK / profile city
  const cityRaw =
    extras.city || project?.cityName || project?.city?.name || null;
  const city = cityRaw ? String(cityRaw).split(",")[0].trim() : null;
  const budget =
    formatBudget(project?.projectBudget) ||
    formatBudget(extras.budget) ||
    null;
  // Prefer ASAP/Flexible from brief body; day-count is a weaker fallback
  const timeline =
    extras.timeline ||
    (typeof project?.projectDuration === "string"
      ? project.projectDuration
      : null) ||
    formatDuration(project?.projectDuration);
  // Listing start/end are the live window — only show real project dates from description
  const projectDates = extras.dates || null;
  const applied =
    !!project?.isApplied ||
    hasApplied(project?.jobId) ||
    (project?.postId ? hasApplied(`post-${project.postId}`) : false);
  const applicationsClosed = isApplicationsClosed(project);

  // Match Post a brief fields — only show rows that have values
  const metaRows = [
    {
      key: "looking",
      Icon: UserSearch,
      label: "Looking for",
      value: creativeType,
    },
    {
      key: "work",
      Icon: Filter,
      label: "Work style",
      value: workStyle,
    },
    {
      key: "city",
      Icon: MapPin,
      label: "City",
      value: city,
    },
    {
      key: "budget",
      Icon: Wallet,
      label: "Budget",
      value: budget,
    },
    {
      key: "dates",
      Icon: CalendarRange,
      label: "Project dates",
      value: projectDates,
    },
    {
      key: "timeline",
      Icon: Clock,
      label: "Timeline",
      value: timeline,
    },
  ].filter((row) => row.value);

  const profileHref = project?.company?.userAliasId
    ? `/user-profile/${project.company.userAliasId}`
    : "#";

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const briefURL = (() => {
    const jobId = String(project?.jobId || "");
    if (jobId.startsWith("post-") && project?.postId) {
      return `${window.location.origin}/posts/${project.postId}`;
    }
    if (project?.postId && (!jobId || jobId.startsWith("post-"))) {
      return `${window.location.origin}/posts/${project.postId}`;
    }
    if (jobId && !jobId.startsWith("post-")) {
      return `${window.location.origin}/projects/${jobId}`;
    }
    return `${window.location.origin}/projects`;
  })();

  const share = async () => {
    try {
      await navigator.clipboard.writeText(briefURL);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
    setMenuOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
      className={`rounded-2xl border p-5 transition-colors flex flex-col ${
        isCreatorBrief && isOwner
          ? "border-[#6C63F5]/25 bg-gradient-to-b from-[rgba(108,99,245,0.08)] to-zinc-900/40 hover:border-[#6C63F5]/40"
          : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
      }`}
    >
      <div className="flex items-center gap-3">
        <Link to={profileHref} className="shrink-0">
          <Avatar src={avatarSrc} name={brand} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-display font-semibold text-zinc-50 truncate">
              {brand}
            </span>
            {isCreatorBrief ? (
              <span className="rounded-full border border-[#6C63F5]/35 bg-[#6C63F5]/10 text-[#c9c5ff] text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5">
                {isOwner ? "You" : "Creator"}
              </span>
            ) : (
              <span className="rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5">
                Brand
              </span>
            )}
            {applicationsClosed && (
              <span className="rounded-full border border-zinc-600 bg-zinc-800 text-zinc-400 text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5">
                Closed
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <span>{timeAgo(project?.createdAt) || "Open brief"}</span>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1 shrink-0">
          {industry && (
            <span className="rounded-full bg-[#6C63F5]/10 border border-[#6C63F5]/25 text-[#8b83ff] text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-1">
              {String(industry).split(",")[0]}
            </span>
          )}
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Brief actions"
              className="text-zinc-500 hover:text-zinc-100 h-8 w-8 rounded-full inline-flex items-center justify-center hover:bg-zinc-800/60 transition-colors"
            >
              <MoreHorizontal size={18} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-1.5 z-30 w-44 rounded-xl border border-zinc-800 bg-zinc-950/95 backdrop-blur-lg shadow-xl overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={share}
                    className="w-full text-left flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-zinc-800"
                  >
                    <Share2 size={15} className="text-[#8b83ff]" /> Share brief
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <p className="mt-4 text-[0.95rem] text-zinc-200 leading-relaxed">
        {needText}
      </p>

      <div className="mt-4 space-y-2 text-sm flex-1">
        {metaRows.map(({ key, Icon, label, value }) => (
          <div key={key} className="flex items-center gap-2 text-zinc-400">
            <Icon size={15} className="text-[#8b83ff] shrink-0" />
            {label} <span className="text-zinc-200">{value}</span>
          </div>
        ))}
      </div>

      {isOwner ? (
        <div className="mt-5 flex flex-col gap-2.5">
          {!compactOwnerActions && !project?._local && (
            <button
              type="button"
              onClick={() =>
                navigate(briefApplicantsPath(project))
              }
              className="brief-owner-btn brief-owner-btn-primary group w-full rounded-full py-2.5 px-4 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all duration-200"
            >
              <span className="brief-owner-btn-icon inline-flex h-7 w-7 items-center justify-center rounded-full shrink-0">
                <Users size={14} />
              </span>
              <span className="flex-1 text-left sm:text-center">
                View applicants
              </span>
              {project?.applicantCount != null && (
                <span className="brief-owner-count tabular-nums rounded-full px-2 py-0.5 text-[0.65rem] font-bold tracking-wide">
                  {project.applicantCount}
                </span>
              )}
              <ArrowRight
                size={14}
                className="brief-owner-arrow opacity-70 group-hover:translate-x-0.5 group-hover:opacity-100 transition-all duration-200 shrink-0"
              />
            </button>
          )}
          <div className="flex items-center gap-2">
            {!compactOwnerActions && !project?._local && !isCreatorBrief && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/projects/edit-project/${project.jobId}`)
                }
                className="brief-owner-btn brief-owner-btn-edit flex-1 rounded-full py-2.5 px-3 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-all duration-200"
              >
                <Pencil size={13} strokeWidth={2.25} /> Edit brief
              </button>
            )}
            <button
              type="button"
              onClick={() => onToggleClosed?.(project, !applicationsClosed)}
              className="brief-owner-btn brief-owner-btn-edit flex-1 rounded-full py-2.5 px-3 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-all duration-200"
            >
              <CircleOff size={13} strokeWidth={2.25} />
              {applicationsClosed ? "Reopen" : "Close applications"}
            </button>
            <button
              type="button"
              onClick={() => onDelete?.(project)}
              className={`brief-owner-btn brief-owner-btn-delete ${
                compactOwnerActions || project?._local || isCreatorBrief
                  ? "flex-1"
                  : "flex-1"
              } rounded-full py-2.5 px-3 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-all duration-200`}
            >
              <Trash2 size={13} strokeWidth={2.25} /> Delete
            </button>
          </div>
        </div>
      ) : applied ? (
        <button
          type="button"
          disabled
          className="mt-5 w-full rounded-full bg-[#6C63F5] text-white py-2.5 text-sm font-semibold opacity-40 cursor-not-allowed"
        >
          Applied
        </button>
      ) : isBrand ? (
        <div className="mt-5 rounded-full bg-zinc-900 border border-dashed border-zinc-800 py-2.5 text-xs text-zinc-500 inline-flex items-center justify-center gap-2">
          Only creatives can apply
        </div>
      ) : applicationsClosed ? (
        <button
          type="button"
          disabled
          className="mt-5 w-full rounded-full bg-zinc-800 text-zinc-400 py-2.5 text-sm font-semibold opacity-70 cursor-not-allowed"
        >
          Application Closed
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onApply?.(project)}
          className="mt-5 w-full rounded-full bg-[#6C63F5] text-white py-2.5 text-sm font-semibold hover:bg-[#5751E5] transition-colors"
        >
          Apply
        </button>
      )}
    </motion.div>
  );
};

const ProjectsFeedPage = () => {
  const dispatch = useDispatch();
  const locationUse = useLocation();
  const { projects } = useSelector((state) => state.projects);
  const { userProfile } = useSelector((state) => state.userProfile);

  const isBrand = userProfile?.userType === "BUSINESS";
  const myId =
    userProfile?.userAliasId ||
    userProfile?.companyResponseDto?.companyAliasId;
  const creatorKey =
    userProfile?.userAliasId || userProfile?.id || userProfile?.userId;
  const urlQ = new URLSearchParams(locationUse.search).get("q") || "";

  const [query, setQuery] = useState(urlQ);
  const [industry, setIndustry] = useState("All");
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [briefOpen, setBriefOpen] = useState(false);
  const [applyingTo, setApplyingTo] = useState(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [myPostedBriefs, setMyPostedBriefs] = useState([]);
  const [creatorOpenBriefs, setCreatorOpenBriefs] = useState([]);
  const itemsPerPage = 10;

  const refreshMyBriefs = useCallback(() => {
    if (isBrand || !creatorKey) {
      setMyPostedBriefs([]);
      return;
    }
    setMyPostedBriefs(getCreatorBriefs(creatorKey));
  }, [isBrand, creatorKey]);

  const fetchCreatorOpenBriefs = useCallback(async () => {
    if (isBrand) {
      setCreatorOpenBriefs([]);
      return;
    }
    try {
      const res = await API.get("api/post", {
        params: {
          pageNo: 1,
          perPage: 40,
          sortBy: "latest",
          tags: "BRIEFS",
        },
      });
      const results = res?.data?.data?.results || [];
      const mapped = results
        .filter(isCreatorBriefPost)
        .map(mapPostToOpenBrief)
        .filter(Boolean);
      setCreatorOpenBriefs(mapped);
    } catch {
      setCreatorOpenBriefs([]);
    }
  }, [isBrand]);

  useEffect(() => {
    refreshMyBriefs();
  }, [refreshMyBriefs]);

  useEffect(() => {
    fetchCreatorOpenBriefs();
  }, [fetchCreatorOpenBriefs]);

  useEffect(() => {
    setQuery(urlQ);
  }, [urlQ]);

  useEffect(() => {
    const params = new URLSearchParams(locationUse.search);
    if (params.get("compose") === "1") {
      setBriefOpen(true);
      params.delete("compose");
      const next = params.toString();
      window.history.replaceState(
        null,
        "",
        next ? `${locationUse.pathname}?${next}` : locationUse.pathname
      );
    }
  }, [locationUse.pathname, locationUse.search]);

  const fetchProjects = useCallback(
    async (reset = false) => {
      setIsLoading(true);
      if (reset) setHasMore(true);
      const currentPage = reset ? 1 : page;
      const res = await dispatch(
        getProjects({
          pageNo: currentPage,
          perPage: itemsPerPage,
          sortBy: "LATEST",
          // QA still auto-filters creators by category when search is blank;
          // '_' is a LIKE wildcard so titles still match while skipping that filter.
          search: (query || "").trim() || (isBrand ? undefined : "_"),
          industry: industry !== "All" ? industry : undefined,
          preference: false,
        })
      );
      const results = res?.payload?.results || [];
      if (results.length < itemsPerPage) setHasMore(false);
      else if (!reset) setPage((p) => p + 1);
      if (reset) setPage(2);
      setIsLoading(false);
    },
    [dispatch, page, query, industry, isBrand]
  );

  useEffect(() => {
    setPage(1);
    fetchProjects(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, industry]);

  const openBriefs = useMemo(() => {
    const brandJobs = projects || [];
    if (isBrand) return brandJobs;

    const q = (query || "").trim().toLowerCase();
    const filteredCreator = creatorOpenBriefs.filter((p) => {
      if (industry !== "All") {
        const ind =
          (Array.isArray(p?.industry) && p.industry[0]) ||
          p?.category?.name ||
          "";
        if (String(ind).toLowerCase() !== industry.toLowerCase()) return false;
      }
      if (!q) return true;
      const hay = [
        p?.jobTitle,
        p?.description,
        p?.company?.name,
        p?.cityName,
        ...(Array.isArray(p?.skillsRequired) ? p.skillsRequired : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });

    const seen = new Set();
    const merged = [];
    for (const p of [...filteredCreator, ...brandJobs]) {
      const key = String(p?.jobId || p?.postId || "");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(p);
    }
    merged.sort(
      (a, b) =>
        new Date(b?.createdAt || 0).getTime() -
        new Date(a?.createdAt || 0).getTime()
    );
    return merged;
  }, [projects, creatorOpenBriefs, isBrand, query, industry]);

  const industryCounts = useMemo(() => {
    const c = { All: openBriefs.length };
    openBriefs.forEach((p) => {
      const key =
        (Array.isArray(p?.industry) && p.industry[0]) ||
        p?.category?.name ||
        "Other";
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [openBriefs]);

  const isOwnerOf = (project) => {
    // Brands only receive their own company jobs from GET api/jobs.
    // CompanyJobRespDto has no alias ids, so ID matching always fails —
    // treat every brief on a brand's "Your briefs" list as owned.
    if (isBrand) return true;
    if (!myId || !project) return false;
    const ownerId =
      project?.company?.userAliasId ||
      project?.companyJobRespDto?.companyAliasId ||
      project?.companyJobRespDto?.userAliasId;
    return !!ownerId && String(ownerId) === String(myId);
  };

  const deleteBrief = async (project) => {
    if (
      !window.confirm(
        "Delete this brief? Applications on it will also be removed."
      )
    )
      return;
    try {
      const postId =
        project?.postId ||
        (String(project?.jobId || "").startsWith("post-")
          ? String(project.jobId).replace(/^post-/, "")
          : null);
      const rawJobId = project?.jobId;
      const isUuidJob =
        rawJobId &&
        !String(rawJobId).startsWith("post-") &&
        /^[0-9a-f-]{36}$/i.test(String(rawJobId));

      if (postId) {
        try {
          await API.delete(`api/post/${postId}`);
        } catch (_) {
          /* post may already be gone */
        }
      }
      if (isUuidJob) {
        await API.delete(`api/jobs/${rawJobId}`);
      }
      removeCreatorBrief(creatorKey, project.jobId);
      toast.success("Brief deleted");
      refreshMyBriefs();
      fetchCreatorOpenBriefs();
      fetchProjects(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to delete"
      );
    }
  };

  const toggleClosed = async (project, closed) => {
    try {
      await setBriefApplicationsClosed(project, closed);
      toast.success(closed ? "Applications closed" : "Applications reopened");
      refreshMyBriefs();
      fetchCreatorOpenBriefs();
      fetchProjects(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not update brief"
      );
    }
  };

  const onBriefPosted = () => {
    refreshMyBriefs();
    if (isBrand) fetchProjects(true);
    else fetchCreatorOpenBriefs();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
      <aside className="hidden lg:block lg:col-span-3 space-y-5 self-start sticky top-24">
        <SidebarCard>
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search briefs…"
              className="w-full rounded-full bg-zinc-900/70 border border-zinc-800 pl-9 pr-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-[#6C63F5]"
            />
          </div>
        </SidebarCard>

        <SidebarCard>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-4">
            <Building2 size={14} className="text-[#8b83ff]" /> Industry
          </div>
          <div className="flex flex-col gap-1 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
            <FilterRow
              active={industry === "All"}
              onClick={() => setIndustry("All")}
              label="All industries"
              count={industryCounts.All}
            />
            {INDUSTRIES.map((i) => (
              <FilterRow
                key={i}
                active={industry === i}
                onClick={() => setIndustry(i)}
                label={i}
                count={industryCounts[i] || 0}
              />
            ))}
          </div>
        </SidebarCard>
      </aside>

      <section className="lg:col-span-9 min-w-0">
        {isBrand && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8b83ff] font-medium mb-2">
                Projects & briefs
              </div>
              <h1 className="font-display font-bold text-4xl tracking-tighter text-zinc-50">
                Your briefs
              </h1>
              <p className="mt-2 text-zinc-400">
                Post new briefs and manage the ones you have live or archived.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setBriefOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#6C63F5] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#5751E5] transition-colors self-start sm:self-auto"
            >
              <Briefcase size={16} /> Post a brief
            </button>
          </div>
        )}

        {!isBrand && (
          <div className="mb-8">
            <div className="projects-posted-band relative overflow-hidden rounded-2xl border mb-0">
              <div
                className="projects-posted-band-glow pointer-events-none absolute inset-0 opacity-70"
                aria-hidden
              />
              <div className="relative flex flex-col sm:flex-row sm:items-end justify-between gap-4 px-5 py-5 sm:px-6 sm:py-6">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.18em] text-[#8b83ff] font-semibold mb-2">
                    <Sparkles size={12} /> Your posts
                  </div>
                  <h2 className="font-display font-bold text-2xl sm:text-[1.65rem] tracking-tight text-zinc-50">
                    Briefs you&apos;ve posted
                  </h2>
                  <p className="mt-1.5 text-sm text-zinc-400 max-w-xl">
                    Collaboration calls you&apos;ve shared — manage them anytime
                    from your posted briefs.
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {myPostedBriefs.length > 0 && (
                    <span className="projects-posted-count rounded-full border px-3 py-1 text-xs tabular-nums">
                      {myPostedBriefs.length}{" "}
                      {myPostedBriefs.length === 1 ? "brief" : "briefs"}
                    </span>
                  )}
                  <Link
                    to="/projects/my-briefs"
                    className="projects-view-all-btn inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors"
                  >
                    View all
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isBrand && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8b83ff] font-medium mb-2">
                Projects & briefs
              </div>
              <h1 className="font-display font-bold text-4xl tracking-tighter text-zinc-50">
                Open briefs
              </h1>
              <p className="mt-2 text-zinc-400">
                Live opportunities from brands and creators. Apply to the ones
                you love.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setBriefOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#6C63F5] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#5751E5] transition-colors self-start sm:self-auto"
            >
              <Briefcase size={16} /> Post a brief
            </button>
          </div>
        )}

        <div className="lg:hidden mb-5 flex items-center gap-3">
          <div className="relative flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search briefs…"
              className="w-full rounded-full bg-zinc-900/70 border border-zinc-800 pl-9 pr-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500"
            />
          </div>
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className={`relative inline-flex items-center gap-2 rounded-full border h-10 px-3.5 text-sm font-medium transition-colors ${
                industry !== "All"
                  ? "border-[#6C63F5] bg-[#6C63F5]/15 text-[#c9c5ff]"
                  : "border-zinc-800 bg-zinc-900/70 text-zinc-300 hover:border-zinc-600"
              }`}
            >
              <Filter size={14} />
              <span className="hidden sm:inline">
                {industry === "All" ? "Industry" : industry.split(",")[0]}
              </span>
              {industry !== "All" && (
                <span className="h-1.5 w-1.5 rounded-full bg-[#6C63F5] sm:hidden" />
              )}
            </button>
            <AnimatePresence>
              {mobileFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setMobileFilterOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 top-full mt-2 z-40 w-64 rounded-xl border border-zinc-800 bg-zinc-950/95 backdrop-blur-lg shadow-2xl overflow-hidden"
                  >
                    <div className="px-3 py-2 text-[0.65rem] uppercase tracking-[0.16em] text-zinc-500 font-semibold border-b border-zinc-800">
                      Filter by industry
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                      {["All", ...INDUSTRIES].map((i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setIndustry(i);
                            setMobileFilterOpen(false);
                          }}
                          className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${
                            industry === i
                              ? "text-[#8b83ff] bg-[#6C63F5]/10 font-medium"
                              : "text-zinc-300 hover:bg-zinc-900"
                          }`}
                        >
                          <span>{i === "All" ? "All industries" : i}</span>
                          {industry === i && (
                            <Check size={13} className="text-[#8b83ff]" />
                          )}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {isLoading && openBriefs.length === 0 ? (
          <div className="py-20 text-center text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading briefs…
          </div>
        ) : !openBriefs.length ? (
          <div className="rounded-2xl border border-dashed border-zinc-800 py-20 text-center text-zinc-500">
            {isBrand
              ? "You haven't posted any briefs yet. Post your first brief to start receiving applications."
              : "No briefs match your filters."}
          </div>
        ) : (
          <InfiniteScroll
            dataLength={openBriefs.length}
            next={() => fetchProjects(false)}
            hasMore={hasMore}
            loader={
              <div className="py-6 text-center text-zinc-500 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading more…
              </div>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {openBriefs.map((project, i) => (
                <ProjectCard
                  key={project?.jobId}
                  project={project}
                  i={i}
                  isBrand={isBrand}
                  isOwner={isOwnerOf(project)}
                  onApply={setApplyingTo}
                  onDelete={deleteBrief}
                  onToggleClosed={toggleClosed}
                />
              ))}
            </div>
          </InfiniteScroll>
        )}
      </section>

      <BriefModal
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        onPosted={onBriefPosted}
      />

      <AnimatePresence>
        {applyingTo && (
          <ApplyDialog
            project={applyingTo}
            onClose={() => setApplyingTo(null)}
            onApplied={() => {
              fetchProjects(true);
              fetchCreatorOpenBriefs();
              setApplyingTo(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectsFeedPage;
